// NodeCommentsField.jsx

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';

import { useUser } from 'context/UserContext';

// Utilities
import { EngagementType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { canUser, PermissionAction } from 'utilities/Permissions';
import { buildUserSummary } from 'utilities/Users';

// Services
import { subscribeToEngagements } from 'services/engagements/EngagementReadService';
import { createEngagement, deleteEngagementsByParams, updateEngagement } from 'services/engagements/EngagementWriteService';
import { nowTimestamp } from 'services/nodes/NodeWriteService';

// CSS
import './NodeCommentsField.css';

// Components
import CommentsAdd from './add/CommentsAdd';
import Comment from './comment/Comment';
import CommentReplyModal from './comment/reply/CommentReplyModal';
import Confirm from 'components/confirm/Confirm';

/**
 * NodeCommentsField
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeCommentsField = ({ node }) => {

    // ----- CONTEXT ----- //

    // User Context

    const {
        user
    } = useUser();

    // ----- REFS ----- //

    // References

    const inputRef = useRef(null);

    // ----- STATE ----- //

    const [comments, setComments] = useState([]);
    const [commentReactions, setCommentReactions] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [addButtonsVisible, setAddButtonsVisible] = useState(false);
    const [showReplies, setShowReplies] = useState({});
    const [replyingTo, setReplyingTo] = useState(null);
    const [expandedReplies, setExpandedReplies] = useState({});
    const [selectedComment, setSelectedComment] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [canComment, setCanComment] = useState(false);
    const [sortedComments, setSortedComments] = useState([]);
    const [replyOpen, setReplyOpen] = useState(false);

    // ----- EFFECTS ----- //

    // Keep engagement state scoped to the actual rendered node.

    useEffect(() => {
        const nodeKey = node?.key;
        if (!nodeKey) {
            setComments([]);
            setCommentReactions([]);
            return;
        }

        const params = [{ field: 'itemKey', operator: '==', value: nodeKey }];
        const unsubscribe = subscribeToEngagements({ params, sortFields: [] }, (engagements) => {
            const commentsOnly = engagements
                .filter((engagement) => engagement.type === EngagementType.NODE_COMMENT)
                .sort((a, b) => {
                    const ad = a?.dateCreated?.toDate?.() ?? 0;
                    const bd = b?.dateCreated?.toDate?.() ?? 0;
                    return ad - bd;
                });

            setComments(commentsOnly.map((comment) => ({ ...comment })));
            setCommentReactions(
                engagements.filter((engagement) => (
                    engagement.type === EngagementType.ACTIVITY_REACTION
                    || engagement.type === EngagementType.COMMENT_LIKE
                ))
            );
        });

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [node?.key]);

    /**
     * Derive permission booleans from canUser.
     */
    useEffect(() => {
        if (!node || !user) {
            setCanComment(false);
            return;
        }
        setCanComment(canUser(PermissionAction.COMMENT, node, user));
    }, [node, user]);

    // ----- MEMOS ----- //

    // Memoize comment likes counts for performance

    const commentReactionsMap = useMemo(() => {
        const map = new Map();
        commentReactions.forEach((row) => {
            const commentKey = String(row?.commentKey || '').trim();
            if (!commentKey) return;
            const count = map.get(commentKey) || 0;
            map.set(commentKey, count + 1);
        });
        return map;
    }, [commentReactions]);

    // Memoize replies count per original key
    const repliesCountMap = useMemo(() => {
        const map = new Map();
        comments.forEach(comment => {
            if (comment.parentKey !== null && comment.originalKey) {
                const count = map.get(comment.originalKey) || 0;
                map.set(comment.originalKey, count + 1);
            }
        });
        return map;
    }, [comments]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setSortedComments((prevSorted) => {
            if (!comments || comments.length === 0) {
                return [];
            }

            // Keys in current incoming snapshot
            const incomingByKey = new Map(comments.map(c => [c.key, c]));

            // 1) Start from prior order, but DROP items that no longer exist remotely
            //    (hard-deleted comments are gone from `comments`)
            const updatedExisting = (prevSorted || [])
                .filter(c => incomingByKey.has(c.key))
                // replace each with the latest version (so deleted/text changes show up)
                .map(c => incomingByKey.get(c.key));

            // 2) Find new top-level comments we haven't seen before (keep your rule)
            const existingKeys = new Set(updatedExisting.map(c => c.key));
            const missingTopLevel = comments.filter(
                c => c.parentKey === null && !existingKeys.has(c.key)
            );

            // 3) Score + order the missing ones (unchanged logic)
            const scoredMissing = missingTopLevel.map(comment => {
                const likesCount = commentReactionsMap.get(comment.key) || 0;
                const repliesCount = repliesCountMap.get(comment.key) || 0;
                const engagementScore = likesCount + repliesCount * 0.5;

                const now = Date.now();
                const commentTime = comment.dateCreated?.toDate?.()?.getTime?.() ?? now;
                const hoursSincePosted = (now - commentTime) / 36e5;
                const recencyScore = Math.max(0, 24 - hoursSincePosted) / 24;

                const creatorBoost = (comment.userKey === node.userKey) ? 0.2 : 0;
                const totalScore = (0.6 * engagementScore) + (0.3 * recencyScore) + creatorBoost;

                return { comment, score: totalScore };
            });

            const sortedMissing = scoredMissing
                .sort((a, b) => b.score - a.score)
                .map(s => s.comment);

            // 4) Final list: prior order (updated objects) + newly inserted
            return [...updatedExisting, ...sortedMissing];
        });
    }, [
        comments,            // <— depend on the full array, not just .length
        commentReactionsMap,
        repliesCountMap,     // use memoized map instead of raw array
        node?.userKey
    ]);

    // ----- HANDLERS ----- //

    /**
     * handleAdd
     *
     * Handles add.
     *
     * @param {string} text - text.
     * @returns {Promise<void>} Resolves when the handler completes.
     */
    const handleAdd = async (text) => {
        const body = (text || '').trim();
        if (!body) return;
        if (!user?.key || !node?.key) return;

        const key = generateKey();
        const now = nowTimestamp();
        const originalKey = replyingTo ? (replyingTo.originalKey || replyingTo.key) : key;
        const userSummary = buildUserSummary(user);

        const data = {
            key,
            userKey: user.key,
            user: userSummary,
            coverUrl: userSummary?.coverUrl || null,
            coverStoragePath: userSummary?.coverStoragePath || null,
            storagePath: userSummary?.storagePath || null,
            itemKey: node.key,
            itemSummary: node,
            parentKey: replyingTo ? replyingTo.key : null,
            originalKey,
            targetKey: node.userKey,
            text: body,
            type: EngagementType.NODE_COMMENT, // replies are comments with parentKey
            dateCreated: now,
        };

        await createEngagement({ key, engagement: data });

        if (replyingTo) {
            const markerKey = generateKey();
            await createEngagement({
                key: markerKey,
                engagement: {
                ...data,
                key: markerKey,
                targetKey: replyingTo.userKey,
                type: EngagementType.COMMENT_REPLY,
                },
            });
        }

        // reset UI
        setNewComment('');
        setAddButtonsVisible(false);
        setReplyingTo(null);
        setReplyOpen(false);
    };

    /**
     * Sets the status of a comment to deleted.
     */
    const handleDeleteConfirmed = async () => {

        setConfirmDeleteOpen(false);

        if (!selectedComment) return;
        if (!user?.key || !node?.key) return;

        // Detect if there are any replies to this comment. If there are,
        // we will not delete the comment, but rather set its status to deleted.
        // Otherwise, we will delete the comment.
        const hasReplies = comments.some(c => c.originalKey === selectedComment.originalKey && c.parentKey !== null);

        if (hasReplies) {
            const updatedComment = {
                ...selectedComment,
                text: '[comment deleted]',
                dateModified: nowTimestamp(),
                deleted: true
            };

            await updateEngagement(selectedComment.key, updatedComment);
        } else {
            const params = [
                { field: 'type', operator: '==', value: EngagementType.NODE_COMMENT },
                { field: 'userKey', operator: '==', value: user.key },
                { field: 'itemKey', operator: '==', value: node.key },
            ];

            await deleteEngagementsByParams(params, { engagementKey: selectedComment.key });
        }
    };

    const handleToggleReplies = useCallback((commentKey) => {
        setShowReplies(prev => ({ ...prev, [commentKey]: !prev[commentKey] }));
    }, []);

    const handleToggleMoreReplies = useCallback((commentKey) => {
        setExpandedReplies(prev => ({ ...prev, [commentKey]: !prev[commentKey] }));
    }, []);

    const handleReplyClicked = useCallback((e, comment) => {
        e.stopPropagation();
        setReplyingTo(comment);
        setReplyOpen(true);
    }, []);

    const handleDeleteClicked = useCallback(async (comment) => {
        setSelectedComment(comment);
        setConfirmDeleteOpen(true);
    }, []);

    // ----- MEMOS ----- //

    // Memoize replies data to avoid recalculation on every render

    const repliesData = useMemo(() => {
        const data = new Map();
        sortedComments.forEach(comment => {
            const replies = comments
                .filter(c => c.originalKey === comment.key && c.parentKey !== null)
                .sort((a, b) => a.dateCreated.toDate() - b.dateCreated.toDate());
            data.set(comment.key, replies);
        });
        return data;
    }, [sortedComments, comments]);

    const renderComments = useCallback(() => {
        return sortedComments.map(comment => {
            const replies = repliesData.get(comment.key) || [];
            const repliesCount = replies.length || 0;
            const displayedReplies = expandedReplies[comment.key] ? replies : replies.slice(0, 3);
            const hasMoreReplies = replies.length > displayedReplies.length;

            return (
                <Comment
                  key={comment.key}
                  node={node}
                  comment={comment}
                  comments={comments}
                  reactions={commentReactions}
                  replies={replies}
                  repliesCount={repliesCount}
                  hasMoreReplies={hasMoreReplies}
                  showReplies={showReplies}
                  displayedReplies={displayedReplies}
                  onDeleteClicked={handleDeleteClicked}
                  onReplyClicked={handleReplyClicked}
                  onToggleMoreReplies={handleToggleMoreReplies}
                  onToggleReplies={handleToggleReplies}
                />
            );
        });
    }, [sortedComments, repliesData, expandedReplies, showReplies, node, comments, commentReactions, handleToggleReplies, handleToggleMoreReplies, handleReplyClicked, handleDeleteClicked]);

    // ----- RENDER ----- //

    return (
        <>
            <div className="node-comments-field-container" >

                {canComment ? (

                    <div className="node-comments-field-add-wrapper">
                        <CommentsAdd
                          node={node}
                          newComment={newComment}
                          addButtonsVisible={addButtonsVisible}
                          inputRef={inputRef}
                          onAddClick={() => handleAdd(newComment)}
                          onChange={setNewComment}
                          onFocus={() => setAddButtonsVisible(true)}
                        />

                    </div>
                ) : (

                    <div
                      className="node-comments-field-add-disabled"
                    >
                        Comments disabled
                    </div>

                )}

                {/* LIST */}
                <div className="node-comments-field-list">{renderComments()}</div>
            </div>

            <Confirm
              title="Confirm Delete"
              message="Are you sure you want to delete this comment?"
              isOpen={confirmDeleteOpen}
              setOpen={setConfirmDeleteOpen}
              onConfirm={handleDeleteConfirmed}
            />

            <CommentReplyModal
              comment={replyingTo}
              isOpen={replyOpen}
              setOpen={setReplyOpen}
              onReplySaved={(text) => handleAdd(text)}
            />

        </>
    );
};

// Dev Tools
NodeCommentsField.displayName = 'NodeCommentsField';

export default NodeCommentsField;
